import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../AppIcon';
import { ROUTES, WA_MESSAGES, openWhatsApp } from '../../config/site';

const NAV_ITEMS = [
  { name: 'Home', path: ROUTES.home, icon: 'Home' },
  { name: 'Games', path: ROUTES.games, icon: 'Gamepad2' },
  { name: 'Winners', path: ROUTES.winners, icon: 'Trophy' },
  { name: 'About', path: ROUTES.about, icon: 'Crown' },
  { name: 'Contact', path: ROUTES.contact, icon: 'Headphones' },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const height = document.body.scrollHeight - window.innerHeight;
      setIsScrolled(y > 24);
      setProgress(height > 0 ? Math.min(100, (y / height) * 100) : 0);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location?.pathname]);

  const isActivePath = (path) => location?.pathname === path;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-header transition-all duration-500 ease-royal ${
          isScrolled
            ? 'border-b border-white/10 bg-[#06060C]/88 backdrop-blur-royal shadow-[0_18px_40px_-30px_rgba(0,0,0,1)]'
            : 'bg-gradient-to-b from-[#06060C]/85 to-transparent'
        }`}
      >
        {/* Announcement rail */}
        <div className="relative overflow-hidden border-b border-royal-gold/15 bg-[#0B0B14]/90">
          <div className="flex whitespace-nowrap py-1.5 text-[0.68rem] tracking-wide text-white/65 animate-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex shrink-0 items-center gap-8 px-4">
                <span className="text-royal-gold">★ Official Nitin Online Book &amp; Nitin Online Book portal</span>
                <span>⚡ Nitin online book login &amp; sign up in 2 minutes</span>
                <span>📲 Nitin online book app download — Android &amp; iOS</span>
                <span className="text-wa-green">💸 ₹50L+ paid out today · 5 minute withdrawals</span>
                <span>👑 24×7 WhatsApp support</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll progress */}
        <div
          className="absolute inset-x-0 bottom-0 h-[2px] origin-left bg-gradient-to-r from-royal-gold via-gold-light to-victory-red transition-transform duration-150"
          style={{ transform: `scaleX(${progress / 100})` }}
          aria-hidden="true"
        />

        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          {/* Brand */}
          <Link to={ROUTES.home} className="group flex items-center gap-3">
            <span className="relative block">
              <span className="absolute -inset-2 rounded-full bg-royal-gold/25 blur-lg transition group-hover:bg-royal-gold/45" aria-hidden="true" />
              <span className="relative flex h-12 w-12 items-center justify-center rounded-full border border-royal-gold/50 bg-gradient-to-br from-[#1A1A28] to-[#08080F] shadow-[0_1px_0_rgba(255,255,255,0.2)_inset,0_16px_30px_-18px_rgba(0,0,0,1)] transition-transform duration-500 group-hover:rotate-[8deg]">
                <img src="/assets/images/logo.png" alt="Nitin Online Book logo" className="h-9 w-9 rounded-full object-contain" />
              </span>
            </span>
            <span className="hidden sm:block leading-none">
              <span className="block font-heading text-xl font-bold gold-text">Nitin Online Book</span>
              <span className="mt-1 block text-[0.62rem] font-semibold uppercase tracking-[0.24em] text-white/45">
                Nitin Online Book
              </span>
            </span>
          </Link>

          {/* Desktop nav — floating glass pill */}
          <nav className="hidden lg:flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.05] p-1.5 backdrop-blur-royal shadow-[0_1px_0_rgba(255,255,255,0.14)_inset,0_20px_40px_-28px_rgba(0,0,0,1)]">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
                  isActivePath(item.path)
                    ? 'bg-gradient-royal text-[#150F02] shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_10px_22px_-12px_rgba(240,194,75,0.9)]'
                    : 'text-white/70 hover:-translate-y-0.5 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon name={item.icon} size={15} />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <span className="flex items-center gap-2 rounded-full border border-wa-green/30 bg-wa-green/10 px-3 py-1.5 text-[0.72rem] font-medium text-white/80">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-wa-green opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-wa-green" />
              </span>
              8,547 online
            </span>

            <button onClick={() => openWhatsApp(WA_MESSAGES.newId)} className="btn-3d shine-hover !px-5 !py-2.5 text-sm">
              <FaWhatsapp size={17} />
              Get Gaming ID
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label="Toggle navigation"
            className="lg:hidden rounded-full border border-white/12 bg-white/5 p-2.5 text-white transition hover:border-royal-gold/60 hover:bg-white/10"
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={22} />
          </button>
        </div>

        {/* Mobile drawer */}
        {isMobileMenuOpen && (
          <div className="lg:hidden animate-rise border-t border-white/10 bg-[#08080F]/97 backdrop-blur-royal">
            <div className="space-y-2 px-4 py-5">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-base font-medium transition ${
                    isActivePath(item.path)
                      ? 'bg-gradient-royal text-[#150F02]'
                      : 'border border-white/8 bg-white/[0.04] text-white/80'
                  }`}
                >
                  <Icon name={item.icon} size={19} />
                  {item.name}
                </Link>
              ))}

              <button
                onClick={() => openWhatsApp(WA_MESSAGES.newId)}
                className="btn-3d btn-3d-wa mt-2 w-full"
              >
                <FaWhatsapp size={19} />
                Get your Gaming ID on WhatsApp
              </button>

              <div className="grid grid-cols-3 gap-2 pt-3 text-center">
                {[
                  { icon: 'ShieldCheck', label: '100% Secure' },
                  { icon: 'Zap', label: '5 Min Payout' },
                  { icon: 'Users', label: '10K+ Players' },
                ].map((chip) => (
                  <div key={chip.label} className="rounded-xl border border-white/8 bg-white/[0.04] px-2 py-2.5">
                    <Icon name={chip.icon} size={15} color="var(--color-primary)" className="mx-auto" />
                    <span className="mt-1 block text-[0.65rem] text-white/60">{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      <div className="h-[6.6rem]" aria-hidden="true" />
    </>
  );
};

export default Header;
